import React, { useState, useEffect, useMemo, useRef, useCallback } from "react";
import {
  useParams,
  useSearchParams,
  useNavigate } from "react-router-dom";
import {
  getProductDetailPath,
  HomepageCategoryMegaMenu,
  type Category,
  HeaderSearchBar,
  Button,
  Input,
  parseCategoryPath,
  buildCategoryPath,
  ProductListHeader,
  ActiveFiltersBar,
  type ActiveFilter,
} from "@b2b/ui-components";
import {
  Search,
  ChevronRight,
  ChevronDown,
  X,
  Building2,
  CheckCircle2,
  Heart,
  Repeat2,
  ShoppingCart,
  Eye,
  SlidersHorizontal,
} from "lucide-react";
import { productService, sellerService, favoritesService } from "@b2b/services";
import { mapProductsToCardFormat } from "../../utils/productMapper";
import { useAuth } from "../../auth/Authprovider";
import { useChatUnreadHeader } from "../../chat/ChatUnreadHeaderProvider";
import { BizcoverIndiaFigmaHeader } from "../../components/marketing/BizcoverIndiaFigmaHeader";
import { BizcoverIndiaFigmaFooter } from "../../components/marketing/BizcoverIndiaFigmaFooter";

// ── Page-singleton TTL caches ──────────────────────────────────────────
// Module-scoped so they survive React unmount/mount within the SPA
// session. Mirrors the request-cache pattern in seller.service.ts /
// admin.service.ts. Also persisted to sessionStorage so F5 within the
// same tab keeps the cache warm — gives the "instant revisit" feel.
const PRODUCT_CACHE_TTL_MS = 60_000;
const CATEGORY_CACHE_TTL_MS = 5 * 60_000;
const PRODUCT_CACHE_MAX_ENTRIES = 50;
const SEARCH_CACHE_STORAGE_KEY = "bz_search_cache_v1";

type ProductCacheEntry = {
  products: any[];
  rawProducts: any[];
  pagination: any | null;
  expiresAt: number;
};

const productListCache = new Map<string, ProductCacheEntry>();
let categoriesCache: { data: any[]; expiresAt: number } | null = null;

// Hydrate from sessionStorage once per JS module load.
(function hydrateSearchCacheFromStorage() {
  try {
    if (typeof window === "undefined" || !window.sessionStorage) return;
    const raw = window.sessionStorage.getItem(SEARCH_CACHE_STORAGE_KEY);
    if (!raw) return;
    const parsed = JSON.parse(raw) as {
      p?: Record<string, ProductCacheEntry>;
      c?: { data: any[]; expiresAt: number } | null;
    };
    const now = Date.now();
    if (parsed?.p && typeof parsed.p === "object") {
      for (const [k, v] of Object.entries(parsed.p)) {
        if (v && typeof v.expiresAt === "number" && v.expiresAt > now) {
          productListCache.set(k, v);
        }
      }
    }
    if (parsed?.c && typeof parsed.c.expiresAt === "number" && parsed.c.expiresAt > now) {
      categoriesCache = parsed.c;
    }
  } catch {
    /* ignore — bad JSON, quota, or sessionStorage disabled */
  }
})();

function persistSearchCacheToStorage(): void {
  try {
    if (typeof window === "undefined" || !window.sessionStorage) return;
    const payload = {
      p: Object.fromEntries(productListCache),
      c: categoriesCache,
    };
    window.sessionStorage.setItem(SEARCH_CACHE_STORAGE_KEY, JSON.stringify(payload));
  } catch {
    /* quota or serialization failure — drop silently */
  }
}

function buildProductCacheKey(
  categoryPath: string | null,
  params: Record<string, unknown>
): string {
  const sortedParams = Object.keys(params)
    .sort()
    .reduce<Record<string, unknown>>((acc, k) => {
      acc[k] = params[k];
      return acc;
    }, {});
  return JSON.stringify({ c: categoryPath ?? null, p: sortedParams });
}

function readProductCache(key: string): ProductCacheEntry | null {
  const entry = productListCache.get(key);
  if (!entry) return null;
  if (Date.now() > entry.expiresAt) {
    productListCache.delete(key);
    return null;
  }
  return entry;
}

function writeProductCache(
  key: string,
  entry: Omit<ProductCacheEntry, "expiresAt">
): void {
  productListCache.set(key, {
    ...entry,
    expiresAt: Date.now() + PRODUCT_CACHE_TTL_MS,
  });
  // Map preserves insertion order — drop the oldest when over the cap.
  while (productListCache.size > PRODUCT_CACHE_MAX_ENTRIES) {
    const oldest = productListCache.keys().next().value;
    if (!oldest) break;
    productListCache.delete(oldest);
  }
  persistSearchCacheToStorage();
}

function readCategoriesCache(): any[] | null {
  if (!categoriesCache) return null;
  if (Date.now() > categoriesCache.expiresAt) {
    categoriesCache = null;
    return null;
  }
  return categoriesCache.data;
}

function writeCategoriesCache(data: any[]): void {
  categoriesCache = {
    data,
    expiresAt: Date.now() + CATEGORY_CACHE_TTL_MS,
  };
  persistSearchCacheToStorage();
}

/** Build the params object the products effect uses, in one place — so the
 *  initial-render cache lookup and the effect compute identical keys. */
function buildProductFetchParams(opts: {
  page: number;
  limit: number;
  searchQuery: string;
  sortBy: string;
  /** When set, passed to GET /products/category/:id as includeDescendants. */
  includeDescendants?: boolean;
  filters?: ProductFilters;
}): Record<string, unknown> {
  const params: Record<string, unknown> = {
    page: opts.page,
    limit: opts.limit,
  };
  if (opts.searchQuery.trim()) params.search = opts.searchQuery.trim();
  if (opts.sortBy === "price-low") {
    params.sortBy = "price";
    params.sortOrder = "asc";
  } else if (opts.sortBy === "price-high") {
    params.sortBy = "price";
    params.sortOrder = "desc";
  } else if (opts.sortBy === "newest") {
    params.sortBy = "createdAt";
    params.sortOrder = "desc";
  } else if (opts.sortBy === "popular") {
    params.sortBy = "popularity";
    params.sortOrder = "desc";
  } else if (opts.sortBy === "rating") {
    params.sortBy = "rating";
    params.sortOrder = "desc";
  }
  if (opts.includeDescendants) {
    params.includeDescendants = true;
  }
  if (opts.filters) {
    applyFiltersToFetchParams(params, opts.filters);
  }
  return params;
}

function getSafePrice(value: unknown): number {
  if (typeof value === "number" && Number.isFinite(value)) return value;
  if (typeof value === "string") {
    const cleaned = value.replace(/[^0-9.]/g, "");
    const parsed = Number.parseFloat(cleaned);
    if (Number.isFinite(parsed)) return parsed;
  }
  return 0;
}

/** Logo + fallback initials for supplier tab cards */
function SupplierTabLogo({
  logo,
  name,
}: {
  logo?: string;
  name: string;
}) {
  const [failed, setFailed] = useState(false);
  const initials = name
    .split(/\s+/)
    .filter(Boolean)
    .slice(0, 2)
    .map((w) => w[0])
    .join("")
    .toUpperCase()
    .slice(0, 2);

  if (!logo || failed) {
    return (
      <div className="w-10 h-10 rounded-lg bg-orange-50 border border-orange-100 flex items-center justify-center text-orange-600 font-semibold text-xs flex-shrink-0">
        {initials || "?"}
      </div>
    );
  }
  return (
    <div className="w-10 h-10 rounded-lg overflow-hidden border border-gray-200 bg-gray-100 flex-shrink-0">
      <img
        src={logo}
        alt=""
        className="w-full h-full object-cover"
        onError={() => setFailed(true)}
      />
    </div>
  );
}

/** Alibaba-style supplier row: details left, products right, footer CTA (list view only). */
function SupplierListCard({
  supplier,
  onViewProfile,
}: {
  supplier: any;
  onViewProfile: () => void;
}) {
  const marketsStr = Array.isArray(supplier.mainMarkets)
    ? supplier.mainMarkets.join(", ")
    : "";

  const contractValue = (() => {
    const o = String(supplier.oemService || "").trim();
    const c = String(supplier.contractManufacturing || "").trim();
    if (o) return o;
    if (c) return c;
    return "";
  })();

  const detailRows: { label: string; value: string }[] = [
    { label: "Business Type", value: String(supplier.businessType || "").trim() || "—" },
    { label: "Main Products", value: String(supplier.mainProducts || "").trim() || "—" },
    {
      label: "Contract Manufacturing",
      value: contractValue || "—",
    },
    { label: "Main Markets", value: marketsStr.trim() || "—" },
    {
      label: "No. of Employees",
      value: String(supplier.employeeRange || "").trim() || "—",
    },
  ];

  return (
    <div className="rounded-xl border border-gray-200 bg-white p-4 shadow-sm md:p-5">
      <div className="flex flex-col gap-6 lg:flex-row lg:items-start lg:justify-between lg:gap-8">
        <div className="min-w-0 flex-1 lg:max-w-[58%]">
          <div className="flex items-start gap-3">
            <SupplierTabLogo logo={supplier.logo} name={supplier.name} />
            <div className="min-w-0 pt-0.5">
              <button
                type="button"
                onClick={onViewProfile}
                className="text-left text-[15px] font-semibold leading-snug text-[#1a365d] underline decoration-[#1a365d]/80 underline-offset-[3px] hover:text-[#ea580c] hover:decoration-[#ea580c] transition-colors"
              >
                {supplier.name}
              </button>
              {supplier.yearsInBusiness != null ? (
                <p className="mt-1 text-sm text-gray-500">
                  {supplier.yearsInBusiness} yrs
                </p>
              ) : null}
            </div>
          </div>

          <dl className="mt-5 space-y-2.5 text-sm">
            {detailRows.map(({ label, value }) => (
              <div
                key={label}
                className="grid grid-cols-1 gap-1 sm:grid-cols-[minmax(0,11rem)_1fr] sm:gap-x-6 sm:items-baseline"
              >
                <dt className="text-gray-500">{label}</dt>
                <dd className="text-gray-900 break-words">{value}</dd>
              </div>
            ))}
          </dl>
        </div>

        <div className="flex flex-shrink-0 flex-wrap gap-3 sm:gap-4 lg:ml-auto lg:justify-end">
          {supplier.products.slice(0, 3).map((p: any) => (
            <div
              key={p.id}
              className="flex w-[104px] flex-col gap-2 sm:w-[118px] md:w-[124px]"
            >
              <div className="aspect-[4/3] w-full overflow-hidden rounded-md border border-gray-200 bg-gray-100">
                {p.image ? (
                  <img
                    src={p.image}
                    alt=""
                    className="h-full w-full object-cover"
                  />
                ) : (
                  <div className="flex h-full w-full items-center justify-center">
                    <Building2 className="h-5 w-5 text-gray-400" />
                  </div>
                )}
              </div>
              <p
                className="text-[11px] leading-snug text-gray-800 line-clamp-3"
                title={p.name}
              >
                {p.name}
              </p>
            </div>
          ))}
        </div>
      </div>

      <div className="mt-5 border-t border-gray-200 pt-4">
        <Button
          type="button"
          size="sm"
          variant="primary"
          className="rounded-full"
          onClick={onViewProfile}
        >
          View Profile
        </Button>
      </div>
    </div>
  );
}

// ── Product filter types (sidebar: categories + price + MOQ only) ────
type ProductFilters = {
  priceMin?: number;
  priceMax?: number;
  moqMin?: number;
  moqMax?: number;
};

const EMPTY_FILTERS: ProductFilters = {};

const PRICE_MAX = 10_000;
const MOQ_MAX = 10_000;

/** Query keys for filters that are intentionally hidden from the UI (strip from URL). */
const LEGACY_FILTER_QUERY_KEYS = [
  "supplierLocation",
  "supplierRating",
  "verifiedOnly",
  "productCondition",
  "certifications",
  "tradeAssurance",
  "freeSamples",
  "customizationAvailable",
  "fastShipping",
  "brands",
  "materialTypes",
  "colors",
  "sizes",
  "grades",
  "includeFacets",
] as const;

function parseFiltersFromSearchParams(sp: URLSearchParams): ProductFilters {
  const parseNum = (raw: string | null): number | undefined => {
    if (raw == null) return undefined;
    const n = Number(raw);
    return Number.isFinite(n) ? n : undefined;
  };

  return {
    priceMin: parseNum(sp.get("priceMin")),
    priceMax: parseNum(sp.get("priceMax")),
    moqMin: parseNum(sp.get("moqMin")),
    moqMax: parseNum(sp.get("moqMax")),
  };
}

function serializeFiltersIntoSearchParams(
  filters: ProductFilters,
  target: URLSearchParams
): void {
  for (const k of LEGACY_FILTER_QUERY_KEYS) {
    target.delete(k);
  }
  const setOrDel = (key: string, val: string | undefined) => {
    if (val == null || val === "") target.delete(key);
    else target.set(key, val);
  };
  setOrDel("priceMin", filters.priceMin != null ? String(filters.priceMin) : undefined);
  setOrDel("priceMax", filters.priceMax != null ? String(filters.priceMax) : undefined);
  setOrDel("moqMin", filters.moqMin != null ? String(filters.moqMin) : undefined);
  setOrDel("moqMax", filters.moqMax != null ? String(filters.moqMax) : undefined);
}

/** Merge filters into the params object passed to productService.getProducts. */
function applyFiltersToFetchParams(
  params: Record<string, unknown>,
  filters: ProductFilters
): void {
  if (filters.priceMin != null) params.priceMin = filters.priceMin;
  if (filters.priceMax != null) params.priceMax = filters.priceMax;
  if (filters.moqMin != null) params.moqMin = filters.moqMin;
  if (filters.moqMax != null) params.moqMax = filters.moqMax;
}

function countActiveFilters(filters: ProductFilters): number {
  let n = 0;
  if (filters.priceMin != null || filters.priceMax != null) n += 1;
  if (filters.moqMin != null || filters.moqMax != null) n += 1;
  return n;
}

// ── Tiny reusable sidebar primitives ─────────────────────────────────
// Inline (page-local) to match the existing Industries/Categories visual
// style (uppercase labels, small spacing) instead of @b2b/ui-components'
// FilterSection which uses larger title styling.

function SidebarFilterGroup({
  title,
  defaultOpen = true,
  children,
}: {
  title: string;
  defaultOpen?: boolean;
  children: React.ReactNode;
}) {
  const [open, setOpen] = useState(defaultOpen);
  return (
    <div>
      <button
        type="button"
        className="mb-2 flex w-full items-center justify-between"
        onClick={() => setOpen((p) => !p)}
      >
        <span className="text-[12px] font-semibold uppercase tracking-wide text-gray-700">
          {title}
        </span>
        <ChevronDown
          className={`h-4 w-4 text-[#6b7280] ${open ? "" : "-rotate-90"}`}
        />
      </button>
      {open && <div>{children}</div>}
    </div>
  );
}

/** Debounced numeric min/max range. Commits up after user pauses typing. */
function RangeFilter({
  min,
  max,
  hardMax,
  onCommit,
  prefix = "",
  debounceMs = 400,
}: {
  min: number | undefined;
  max: number | undefined;
  hardMax: number;
  onCommit: (min: number | undefined, max: number | undefined) => void;
  prefix?: string;
  debounceMs?: number;
}) {
  const [localMin, setLocalMin] = useState<string>(min != null ? String(min) : "");
  const [localMax, setLocalMax] = useState<string>(max != null ? String(max) : "");
  const timerRef = useRef<NodeJS.Timeout | null>(null);
  // Keep local state in sync with external value (URL pop, chip clear, etc.)
  useEffect(() => {
    setLocalMin(min != null ? String(min) : "");
  }, [min]);
  useEffect(() => {
    setLocalMax(max != null ? String(max) : "");
  }, [max]);

  const scheduleCommit = (nextMin: string, nextMax: string) => {
    if (timerRef.current) clearTimeout(timerRef.current);
    timerRef.current = setTimeout(() => {
      const parsedMin = nextMin === "" ? undefined : Math.max(0, Number(nextMin));
      const parsedMax =
        nextMax === "" ? undefined : Math.min(hardMax, Number(nextMax));
      const safeMin = parsedMin != null && Number.isFinite(parsedMin) ? parsedMin : undefined;
      const safeMax = parsedMax != null && Number.isFinite(parsedMax) ? parsedMax : undefined;
      onCommit(safeMin, safeMax);
    }, debounceMs);
  };

  useEffect(() => {
    return () => {
      if (timerRef.current) clearTimeout(timerRef.current);
    };
  }, []);

  return (
    <div className="flex items-center gap-2">
      <div className="flex-1">
        <label className="mb-1 block text-[11px] text-gray-500">
          Min{prefix ? ` (${prefix})` : ""}
        </label>
        <input
          type="number"
          inputMode="numeric"
          min={0}
          max={hardMax}
          value={localMin}
          onChange={(e) => {
            setLocalMin(e.target.value);
            scheduleCommit(e.target.value, localMax);
          }}
          placeholder="0"
          className="h-8 w-full rounded border border-[#e6ebf0] px-2 text-xs outline-none focus:border-[#F97316]"
        />
      </div>
      <span className="mt-5 text-gray-400">-</span>
      <div className="flex-1">
        <label className="mb-1 block text-[11px] text-gray-500">
          Max{prefix ? ` (${prefix})` : ""}
        </label>
        <input
          type="number"
          inputMode="numeric"
          min={0}
          max={hardMax}
          value={localMax}
          onChange={(e) => {
            setLocalMax(e.target.value);
            scheduleCommit(localMin, e.target.value);
          }}
          placeholder={String(hardMax)}
          className="h-8 w-full rounded border border-[#e6ebf0] px-2 text-xs outline-none focus:border-[#F97316]"
        />
      </div>
    </div>
  );
}

const ProductSearch = () => {
  const { subcategory } = useParams();
  const navigate = useNavigate();
  const headerWrapperRef = useRef<HTMLDivElement | null>(null);
  const { logout, userRole: authUserRole, isAuthenticated } = useAuth();
  const chatUnread = useChatUnreadHeader();
  const [searchParams, setSearchParams] = useSearchParams();
  const categoryPathParam = searchParams.get("categoryPath");

  useEffect(() => {
    window.scrollTo(0, 0);
  }, [subcategory, searchParams.toString()]);

  // Extract search query from route parameter (subcategory) or query string
  // Route: /search/Peanuts -> subcategory = "Peanuts"
  // Query: /search?query=Peanuts -> query param = "Peanuts"
  const routeQuery = subcategory ? decodeURIComponent(subcategory) : "";
  const queryParam = searchParams.get("query") || "";
  const initialQuery = routeQuery || queryParam;

  const initialSortBy = searchParams.get("sortBy") || "relevance";
  const initialLimit = parseInt(searchParams.get("limit") || "20");
  const initialPage = parseInt(searchParams.get("page") || "1");
  const initialFilters = parseFiltersFromSearchParams(searchParams);

  // Cache lookup happens BEFORE useState so the very first render already
  // has data — Alibaba-style "instant revisit" with no loading flicker.
  const initialCacheKey = buildProductCacheKey(
    categoryPathParam,
    buildProductFetchParams({
      page: initialPage,
      limit: initialLimit,
      searchQuery: initialQuery,
      sortBy: initialSortBy,
      includeDescendants: Boolean(categoryPathParam),
      filters: initialFilters,
    })
  );
  const initialProductCache = readProductCache(initialCacheKey);

  const [searchQuery, setSearchQuery] = useState(initialQuery);
  const [sortBy, setSortBy] = useState(initialSortBy);
  const [itemsPerPage, setItemsPerPage] = useState(initialLimit);
  const [filters, setFilters] = useState<ProductFilters>(initialFilters);
  const [showMobileFilters, setShowMobileFilters] = useState(false);

  const [products, setProducts] = useState<any[]>(
    () => initialProductCache?.products ?? []
  );
  const [rawProducts, setRawProducts] = useState<any[]>(
    () => initialProductCache?.rawProducts ?? []
  );

  const [megaMenuOpen, setMegaMenuOpen] = useState(false);
  const [megaMenuHoveredCategoryId, setMegaMenuHoveredCategoryId] = useState<string | null>(null);
  const [megaMenuTopOffset, setMegaMenuTopOffset] = useState(100);
  const [activeTab] = useState<"products" | "suppliers">("products");
  const [suppliersView, setSuppliersView] = useState<"grid" | "list">("grid");
  const [hoveredSupplierId, setHoveredSupplierId] = useState<string | null>(
    null
  );
  // Default loading=true on a true cold start (nothing cached) so we show the
  // spinner instead of an "empty state" flash; cache hits start at false.
  const [loading, setLoading] = useState(() => !initialProductCache);
  const [error, setError] = useState<string | null>(null);
  const [categories, setCategories] = useState<any[]>(
    () => readCategoriesCache() ?? []
  );
  const [favoriteIds, setFavoriteIds] = useState<Set<string>>(new Set());
  const [favoriteLoadingIds, setFavoriteLoadingIds] = useState<Set<string>>(new Set());
  const [categoryId, setCategoryId] = useState<string | null>(null);
  const [parsedCategoryPath, setParsedCategoryPath] = useState<any[]>([]);
  const [hasSearched, setHasSearched] = useState(false); // Track if user has explicitly searched
  const [isSidebarIndustryExpanded, setIsSidebarIndustryExpanded] = useState(true);
  const [isSidebarCategoryExpanded, setIsSidebarCategoryExpanded] = useState(true);
  const [showAllSidebarIndustries, setShowAllSidebarIndustries] = useState(false);
  const [sidebarIndustrySearch, setSidebarIndustrySearch] = useState("");
  const [showAllSidebarCategories, setShowAllSidebarCategories] = useState(false);
  const [sidebarCategorySearch, setSidebarCategorySearch] = useState("");
  type PaginationState = {
    page: number;
    limit: number;
    total: number;
    totalPages: number;
    hasNext: boolean;
    hasPrev: boolean;
  };
  const [pagination, setPagination] = useState<PaginationState>(() => {
    const base: PaginationState = {
      page: initialPage,
      limit: initialLimit,
      total: 0,
      totalPages: 0,
      hasNext: false,
      hasPrev: false,
    };
    return initialProductCache?.pagination
      ? { ...base, ...initialProductCache.pagination }
      : base;
  });
  const canShowFavorites = isAuthenticated && authUserRole === "buyer";

  // Update searchQuery when route parameter or query param changes
  useEffect(() => {
    const newQuery = routeQuery || queryParam;
    if (newQuery && newQuery !== searchQuery) {
      setSearchQuery(newQuery);
      // If there's a query in URL, mark as searched (user came from search link)
      setHasSearched(true);
    } else if (!newQuery && searchQuery) {
      // Clear search if route/query param is removed
      setSearchQuery("");
      setHasSearched(false);
    }
  }, [routeQuery, queryParam]); // eslint-disable-line react-hooks/exhaustive-deps

  // Fetch categories and parse categoryPath
  useEffect(() => {
    const applyCategories = (cats: any[]) => {
      setCategories(cats);
      if (!categoryPathParam) {
        setParsedCategoryPath([]);
        setCategoryId(null);
        return;
      }
      const parsed = parseCategoryPath(categoryPathParam, cats);
      if (parsed && parsed.length > 0) {
        setParsedCategoryPath(parsed);
        setCategoryId(parsed[parsed.length - 1].id);
      } else {
        setParsedCategoryPath([]);
        setCategoryId(null);
      }
    };

    const cached = readCategoriesCache();
    if (cached) {
      applyCategories(cached);
      return;
    }

    sellerService
      .getCategories(true)
      .then((cats) => {
        writeCategoriesCache(cats);
        applyCategories(cats);
      })
      .catch((err) => {
        console.error("Error fetching categories:", err);
      });
  }, [categoryPathParam]);

  useEffect(() => {
    let active = true;
    if (!canShowFavorites) {
      setFavoriteIds(new Set());
      return;
    }
    favoritesService
      .getFavorites()
      .then((response: any) => {
        if (!active) return;
        const favorites = response?.favorites || response?.data?.favorites || [];
        setFavoriteIds(new Set((favorites || []).map((f: any) => String(f.id))));
      })
      .catch(() => {
        if (!active) return;
        setFavoriteIds(new Set());
      });
    return () => {
      active = false;
    };
  }, [canShowFavorites]);

  // Update URL when search/sort/pagination/filters change
  useEffect(() => {
    const params = new URLSearchParams();
    if (categoryPathParam) params.set("categoryPath", categoryPathParam);
    // Only add query to URL params if it's NOT already in the route parameter
    // This prevents duplicate: /search/Peanuts?query=Peanuts
    if (searchQuery && !routeQuery) {
      params.set("query", searchQuery);
    }
    if (sortBy !== "relevance") params.set("sortBy", sortBy);
    if (pagination.page > 1) params.set("page", pagination.page.toString());
    if (itemsPerPage !== 20) params.set("limit", itemsPerPage.toString());
    serializeFiltersIntoSearchParams(filters, params);

    setSearchParams(params, { replace: true });
  }, [
    sortBy,
    pagination.page,
    itemsPerPage,
    searchQuery,
    categoryPathParam,
    routeQuery, // Include routeQuery to prevent duplicate query param
    filters,
  ]);

  // Fetch products from API (with module-scoped TTL cache for instant repeats)
  useEffect(() => {
    // Wait for categories to load if categoryPathParam is set
    if (categoryPathParam && !categoryId && categories.length === 0) {
      return;
    }

    const params = buildProductFetchParams({
      page: pagination.page,
      limit: pagination.limit,
      searchQuery,
      sortBy,
      includeDescendants: Boolean(categoryId),
      filters,
    });

    // Cache key uses the URL-stable categoryPathParam (not the resolved
    // categoryId) so revisits hit the cache before category resolution.
    const cacheKey = buildProductCacheKey(categoryPathParam, params);
    const cached = readProductCache(cacheKey);
    if (cached) {
      setProducts(cached.products);
      setRawProducts(cached.rawProducts);
      if (cached.pagination) setPagination(cached.pagination);
      setError(null);
      setLoading(false);
      return;
    }

    let active = true;
    setLoading(true);
    setError(null);

    const productPromise = categoryId
      ? productService.getProductsByCategory(categoryId, params)
      : productService.getProducts(params);

    productPromise
      .then((response) => {
        if (!active) return;
        // Handle both wrapped and unwrapped response structures
        const responseData = (response as any).data || response;
        if (responseData && responseData.success !== false) {
          const productsList =
            responseData.products || responseData.data?.products || [];
          const mappedProducts = mapProductsToCardFormat(productsList);
          const paginationData =
            responseData.pagination || responseData.data?.pagination || null;

          setProducts(mappedProducts);
          setRawProducts(productsList);
          if (paginationData) {
            setPagination(paginationData);
          }

          writeProductCache(cacheKey, {
            products: mappedProducts,
            rawProducts: productsList,
            pagination: paginationData,
          });
        } else {
          setError("Failed to load products");
        }
        setLoading(false);
      })
      .catch((err) => {
        if (!active) return;
        console.error("Error fetching products:", err);
        setError(`Failed to load products: ${err?.message || "Unknown error"}`);
        setLoading(false);
      });

    return () => {
      active = false;
    };
  }, [
    pagination.page,
    pagination.limit,
    searchQuery,
    sortBy,
    categoryPathParam,
    categoryId,
    categories.length,
    hasSearched, // Include hasSearched to trigger fetch when user clicks search
    routeQuery, // Include routeQuery to trigger fetch when URL has query
    queryParam, // Include queryParam to trigger fetch when URL has query
    filters,
  ]);

  // Get category info for filters
  const currentCategory = parsedCategoryPath[parsedCategoryPath.length - 1];
  const subcategories = currentCategory?.children || [];
  const categoryIdToName = useMemo(() => {
    const map = new Map<string, string>();
    const walk = (nodes: any[]) => {
      nodes.forEach((node) => {
        if (node?.id && node?.name) map.set(String(node.id), String(node.name));
        if (Array.isArray(node?.children) && node.children.length > 0) {
          walk(node.children);
        }
      });
    };
    if (Array.isArray(categories) && categories.length > 0) walk(categories);
    return map;
  }, [categories]);
  const parentCategory =
    parsedCategoryPath.length > 1
      ? parsedCategoryPath[parsedCategoryPath.length - 2]
      : null;
  const siblingCategories = parentCategory?.children || [];

  // ── Filter mutation helpers ────────────────────────────────────────
  // Defined here (before category handlers) so handleSidebarClearCategories
  // can reference handleClearFilters. Every mutation resets pagination to
  // page 1 so users see the first page of the new result set.
  const updateFilters = useCallback((patch: Partial<ProductFilters>) => {
    setFilters((prev) => ({ ...prev, ...patch }));
    setPagination((prev) => ({ ...prev, page: 1 }));
  }, []);

  const handleClearFilters = useCallback(() => {
    setFilters(EMPTY_FILTERS);
    setPagination((prev) => ({ ...prev, page: 1 }));
  }, []);

  const commitCategoryPathNodes = useCallback(
    (nodes: any[] | null) => {
      setPagination((prev) => ({ ...prev, page: 1 }));
      setSearchParams((prev) => {
        const next = new URLSearchParams(prev);
        if (nodes && nodes.length > 0) {
          next.set("categoryPath", buildCategoryPath(nodes));
        } else {
          next.delete("categoryPath");
        }
        next.delete("page");
        return next;
      }, { replace: true });
    },
    [setSearchParams]
  );

  const handleSidebarClearCategories = useCallback(() => {
    setSidebarIndustrySearch("");
    setSidebarCategorySearch("");
    setShowAllSidebarIndustries(false);
    setShowAllSidebarCategories(false);
    commitCategoryPathNodes(null);
    handleClearFilters();
  }, [commitCategoryPathNodes, handleClearFilters]);

  const topIndustries = useMemo(() => {
    if (!Array.isArray(categories) || categories.length === 0) return [];
    return categories
      .filter((c: any) => !c.parentId)
      .slice()
      .sort((a: any, b: any) =>
        String(a.name || "").localeCompare(String(b.name || ""), undefined, {
          sensitivity: "base",
        })
      );
  }, [categories]);

  const sidebarIndustryFilterList = useMemo(() => {
    const q = sidebarIndustrySearch.trim().toLowerCase();
    const list = q
      ? topIndustries.filter((c: any) =>
          String(c.name || "")
            .toLowerCase()
            .includes(q)
        )
      : topIndustries;
    return showAllSidebarIndustries ? list : list.slice(0, 8);
  }, [topIndustries, sidebarIndustrySearch, showAllSidebarIndustries]);

  const industryForSidebar = parsedCategoryPath[0];
  const sidebarSecondaryFilterList = useMemo(() => {
    const raw =
      industryForSidebar?.children && Array.isArray(industryForSidebar.children)
        ? industryForSidebar.children
        : [];
    const sorted = raw
      .slice()
      .sort((a: any, b: any) =>
        String(a.name || "").localeCompare(String(b.name || ""), undefined, {
          sensitivity: "base",
        })
      );
    const q = sidebarCategorySearch.trim().toLowerCase();
    const list = q
      ? sorted.filter((c: any) =>
          String(c.name || "")
            .toLowerCase()
            .includes(q)
        )
      : sorted;
    return showAllSidebarCategories ? list : list.slice(0, 8);
  }, [industryForSidebar, sidebarCategorySearch, showAllSidebarCategories]);

  const selectedSecondaryId =
    parsedCategoryPath.length > 1 ? String(parsedCategoryPath[1].id) : "";

  const handleSearchChange = (value: string) => {
    setSearchQuery(value);
    setPagination((prev) => ({ ...prev, page: 1 }));
  };

  const handleSearch = () => {
    setHasSearched(true); // Mark that user has explicitly searched
    setPagination((prev) => ({ ...prev, page: 1 }));
  };

  const handleClearSearch = () => {
    setSearchQuery("");
    setHasSearched(false); // Reset search state when clearing
    // Don't clear products - will fetch all products when search is empty
    setPagination((prev) => ({ ...prev, page: 1 }));
  };

  const handlePageChange = (newPage: number) => {
    if (newPage >= 1 && newPage <= pagination.totalPages) {
      setPagination((prev) => ({ ...prev, page: newPage }));
      window.scrollTo({ top: 0, behavior: "smooth" });
    }
  };

  const handleSortChange = (value: string) => {
    setSortBy(value);
    setPagination((prev) => ({ ...prev, page: 1 }));
  };

  const activeFilters: ActiveFilter[] = useMemo(() => {
    const list: ActiveFilter[] = [];
    if (filters.priceMin != null || filters.priceMax != null) {
      const lo = filters.priceMin ?? 0;
      const hi = filters.priceMax ?? PRICE_MAX;
      list.push({
        key: "price",
        label: "Price",
        value: `$${lo} - $${hi}`,
        onRemove: () => updateFilters({ priceMin: undefined, priceMax: undefined }),
      });
    }
    if (filters.moqMin != null || filters.moqMax != null) {
      const lo = filters.moqMin ?? 0;
      const hi = filters.moqMax ?? MOQ_MAX;
      list.push({
        key: "moq",
        label: "MOQ",
        value: `${lo} - ${hi}`,
        onRemove: () => updateFilters({ moqMin: undefined, moqMax: undefined }),
      });
    }
    return list;
  }, [filters, updateFilters]);

  const activeFilterCount = activeFilters.length;

  const handleContactSupplier = (productId: string) => {
    navigate(getProductDetailPath(productId));
  };

  const handleToggleFavorite = async (id: string) => {
    if (!canShowFavorites) return;
    const productId = String(id);
    const currentlyFavorited = favoriteIds.has(productId);
    const previous = new Set(favoriteIds);

    setFavoriteLoadingIds((prev) => new Set(prev).add(productId));
    setFavoriteIds((prev) => {
      const next = new Set(prev);
      if (currentlyFavorited) next.delete(productId);
      else next.add(productId);
      return next;
    });

    try {
      if (currentlyFavorited) await favoritesService.removeFavorite(productId);
      else await favoritesService.addFavorite(productId);
    } catch {
      setFavoriteIds(previous);
    } finally {
      setFavoriteLoadingIds((prev) => {
        const next = new Set(prev);
        next.delete(productId);
        return next;
      });
    }
  };

  const handleViewSupplierProfile = (supplierId: string) => {
    navigate(`/seller_detail/${supplierId}`);
  };

  const handleBreadcrumbClick = (index: number) => {
    if (index === 0) {
      navigate("/");
    } else if (index < parsedCategoryPath.length) {
      const pathToNavigate = parsedCategoryPath.slice(0, index + 1);
      const pathString = buildCategoryPath(pathToNavigate);
      navigate(`/search?categoryPath=${encodeURIComponent(pathString)}`);
    }
  };

  const suppliers = useMemo(() => {
    const normalizeBusinessType = (v: unknown) => {
      const s = String(v || "").toLowerCase();
      if (!s) return "";
      if (s.includes("manufacturer")) return "Manufacturer";
      if (s.includes("distributor") || s.includes("wholesaler"))
        return "Wholesaler";
      if (s.includes("trading")) return "Trading Company";
      return String(v);
    };

    const normalizeMainProducts = (v: unknown) => {
      const toIndustryLabel = (item: unknown): string => {
        if (item == null) return "";
        if (typeof item === "string") {
          const s = item.trim();
          if (!s) return "";
          return categoryIdToName.get(s) || s;
        }
        if (typeof item === "object") {
          const obj = item as Record<string, unknown>;
          const name =
            obj.name ??
            obj.label ??
            obj.title ??
            (typeof obj.industryName === "string" ? obj.industryName : undefined);
          if (typeof name === "string" && name.trim()) return name.trim();
          const id = obj.id ?? obj.value ?? obj.industryId ?? obj.categoryId;
          if (typeof id === "string" && id.trim()) {
            return categoryIdToName.get(id.trim()) || id.trim();
          }
        }
        return "";
      };

      if (v == null) return "";
      if (Array.isArray(v)) {
        return v
          .map(toIndustryLabel)
          .filter(Boolean)
          .slice(0, 6)
          .join(", ");
      }
      if (typeof v === "object") {
        const o = v as Record<string, unknown>;
        const nested = o.industryTypes ?? o.industries ?? o.mainProducts;
        if (Array.isArray(nested)) {
          return nested
            .map(toIndustryLabel)
            .filter(Boolean)
            .slice(0, 6)
            .join(", ");
        }
      }
      const s = String(v).trim();
      if (!s) return "";
      // If it's already comma-separated, keep the first chunk(s).
      if (s.includes(",")) return s.split(",").map((x) => x.trim()).filter(Boolean).slice(0, 6).join(", ");
      return s;
    };

    const normalizeStringList = (v: unknown) => {
      if (v == null) return [];
      if (Array.isArray(v)) return v.map(String).map((x) => x.trim()).filter(Boolean);
      if (typeof v === "object") {
        const o = v as Record<string, unknown>;
        const nested = o.mainMarkets ?? o.markets ?? o.regions;
        if (Array.isArray(nested)) {
          return nested.map(String).map((x) => x.trim()).filter(Boolean);
        }
      }
      const s = String(v).trim();
      if (!s) return [];
      return s.split(/[,;|]/).map((x) => x.trim()).filter(Boolean);
    };

    const employeeRangeLabel = (countRaw: unknown) => {
      const n = typeof countRaw === "number" ? countRaw : parseInt(String(countRaw || ""), 10);
      if (!Number.isFinite(n) || n <= 0) return "";
      if (n <= 10) return "1-10";
      if (n <= 50) return "11-50";
      if (n <= 100) return "51-100";
      if (n <= 200) return "101-200";
      if (n <= 1000) return "201-1000";
      return "1000+";
    };

    const toAvailabilityLabel = (v: unknown) => {
      const s = String(v || "").toLowerCase().trim();
      if (!s) return "";
      if (["yes", "true", "available", "provided", "oem", "on", "1"].some((k) => s.includes(k)))
        return "Available";
      // some APIs return already as human readable
      return v ? String(v) : "";
    };

    const yearsInBusinessFromFoundedYear = (foundedYear: unknown): number | null => {
      const y =
        typeof foundedYear === "number"
          ? foundedYear
          : parseInt(String(foundedYear ?? "").trim(), 10);
      if (!Number.isFinite(y) || y < 1800) return null;
      const current = new Date().getFullYear();
      if (y > current + 1) return null;
      const yrs = current - y;
      if (yrs < 0) return null;
      return Math.max(1, yrs);
    };

    return rawProducts.reduce((acc: any[], product: any) => {
      const supplierSource =
        product.vendor || product.company || product.assignedSeller;
      if (!supplierSource) return acc;

      const company = supplierSource.company || supplierSource;
      /** Full company row on the product (always the product owner), for fields missing on assignedSeller */
      const productCompany = product.company as
        | Record<string, unknown>
        | undefined
        | null;

      // Prefer nested company on vendor; also use root product.company (e.g. when supplierSource is assignedSeller)
      const supplierLogo =
        (company as { logo?: string }).logo ||
        (supplierSource as { logo?: string }).logo ||
        (supplierSource as { companyLogo?: string }).companyLogo ||
        (product.company as { logo?: string } | undefined)?.logo ||
        "";

      const profileSellerId =
        product.assignedSellerId ||
        product.assignedSeller?.id ||
        (Array.isArray(product.company?.userProfiles)
          ? product.company.userProfiles.find(
              (u: any) =>
                u?.role === "SELLER" ||
                u?.role === "BUYER_SELLER" ||
                u?.sellerStatus === "APPROVED"
            )?.id
          : undefined);

      const supplierId =
        profileSellerId ||
        supplierSource.id ||
        supplierSource.userId ||
        supplierSource.companyId ||
        supplierSource.name;
      if (!supplierId) return acc;

      const existing = acc.find((s) => s.id === String(supplierId));
      const normalizedProduct = {
        id: product.id,
        name: product.name,
        image:
          product.thumbnailImage ||
          product.images?.[0]?.url ||
          product.images?.[0] ||
          "",
      };

      const businessType = normalizeBusinessType(
        supplierSource.businessType ??
          (company as { businessType?: unknown }).businessType ??
          productCompany?.businessType ??
          supplierSource.type
      );
      const mainProducts = normalizeMainProducts(
        (supplierSource as any).industryTypes ??
          (company as any).industryTypes ??
          productCompany?.industryTypes ??
          (supplierSource as any).industry ??
          (company as any).industry ??
          productCompany?.industry ??
          (supplierSource as any).mainProducts ??
          (company as any).mainProducts ??
          (company as any).description ??
          productCompany?.description
      );

      const contractManufacturing = toAvailabilityLabel(
        (supplierSource as any).contractManufacturing ??
          (company as any).contractManufacturing ??
          productCompany?.contractManufacturing
      );

      // No dedicated OEM field exists in the current API types;
      // treat OEM service as contract manufacturing when present.
      const oemService = toAvailabilityLabel(
        (supplierSource as any).oemService ??
          (company as any).oemService ??
          (supplierSource as any).contractManufacturing ??
          (company as any).contractManufacturing ??
          productCompany?.oemService ??
          productCompany?.contractManufacturing
      );

      const mainMarkets = normalizeStringList(
        (supplierSource as any).mainMarkets ??
          (company as any).mainMarkets ??
          productCompany?.mainMarkets
      );

      const employeeRange = employeeRangeLabel(
        (supplierSource as any).employeeCount ??
          (company as any).employeeCount ??
          productCompany?.employeeCount
      );

      const foundedYearRaw =
        (company as { foundedYear?: unknown }).foundedYear ??
        (supplierSource as { foundedYear?: unknown }).foundedYear ??
        productCompany?.foundedYear;
      const yearsInBusiness = yearsInBusinessFromFoundedYear(foundedYearRaw);

      const countryLabel =
        [supplierSource.country, supplierSource.countryName, productCompany?.country as string | undefined]
          .map((x) => (typeof x === "string" ? x.trim() : ""))
          .find(Boolean) || "Global";

      const cityLabel =
        [supplierSource.city, supplierSource.location, productCompany?.city as string | undefined]
          .map((x) => (typeof x === "string" ? x.trim() : ""))
          .find(Boolean) || "";

      const supplierDisplayName =
        [supplierSource.name, supplierSource.companyName, productCompany?.name as string | undefined]
          .map((x) => (typeof x === "string" ? x.trim() : ""))
          .find(Boolean) || "Supplier";

      if (existing) {
        existing.products.push(normalizedProduct);
        if (!existing.profileSellerId && profileSellerId) {
          existing.profileSellerId = String(profileSellerId);
        }
        if (!existing.logo && supplierLogo) existing.logo = supplierLogo;
        if (existing.country === "Global" && countryLabel !== "Global") {
          existing.country = countryLabel;
        }
        if (!existing.city && cityLabel) existing.city = cityLabel;
        if (!existing.businessType && businessType) existing.businessType = businessType;
        if (!existing.mainProducts && mainProducts) existing.mainProducts = mainProducts;
        if (!existing.contractManufacturing && contractManufacturing)
          existing.contractManufacturing = contractManufacturing;
        if (!existing.oemService && oemService) existing.oemService = oemService;
        if ((!existing.mainMarkets || existing.mainMarkets.length === 0) && mainMarkets.length > 0)
          existing.mainMarkets = mainMarkets;
        if (!existing.employeeRange && employeeRange) existing.employeeRange = employeeRange;
        if (existing.yearsInBusiness == null && yearsInBusiness != null) {
          existing.yearsInBusiness = yearsInBusiness;
        }
        return acc;
      }

      acc.push({
        id: String(supplierId),
        profileSellerId: profileSellerId ? String(profileSellerId) : "",
        logo: supplierLogo || "",
        name: supplierDisplayName,
        country: countryLabel,
        city: cityLabel,
        yearsInBusiness: yearsInBusiness ?? null,
        businessType: businessType || "",
        mainProducts: mainProducts || "",
        contractManufacturing: contractManufacturing || "",
        oemService: oemService || "",
        mainMarkets,
        employeeRange: employeeRange || "",
        products: [normalizedProduct],
      });
      return acc;
    }, []);
  }, [rawProducts, categoryIdToName]);
  const headerSearchBarComponent = (
    <HeaderSearchBar
      searchQuery={searchQuery}
      onSearchChange={handleSearchChange}
      onSearch={handleSearch}
      onClear={handleClearSearch}
    />
  );
  // const HeaderSearchBar = (
  //   <div className="w-full max-w-3xl">
  //     <div className="flex gap-2">
  //       <div className="flex-1 relative">
  //         <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-5 w-5 text-gray-400 pointer-events-none" />
  
  //         <Input
  //           type="text"
  //           placeholder="Search products, suppliers..."
  //           value={searchQuery}
  //           onChange={(e) => handleSearchChange(e.target.value)}
  //           onKeyDown={(e) => {
  //             if (e.key === "Enter") handleSearch();
  //           }}
  //           className="pl-10 pr-10 h-10 text-sm" // 👈 reduced height for header
  //         />
  
  //         {searchQuery && (
  //           <button
  //             onClick={handleClearSearch}
  //             className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600"
  //             type="button"
  //           >
  //             <X className="h-4 w-4" />
  //           </button>
  //         )}
  //       </div>
  
  //       <Button
  //         onClick={handleSearch}
  //         variant="primary"
  //         className="h-10 px-6 whitespace-nowrap bg-[#F97316] hover:bg-[#ea6820]"
  //       >
  //         <Search className="h-4 w-4 mr-2" />
  //         Search
  //       </Button>
  //     </div>
  //   </div>
  // );

  const sortedSuppliers = useMemo(() => {
    // Supplier objects are derived from the current products page.
    // Since we don't reliably have price/date/rating at supplier aggregation level,
    // we provide a consistent, deterministic fallback sort using product count.
    if (sortBy === "relevance") return suppliers;

    const copy = [...suppliers];
    copy.sort((a: any, b: any) => {
      const aCount = Array.isArray(a.products) ? a.products.length : 0;
      const bCount = Array.isArray(b.products) ? b.products.length : 0;

      // Common "ascending" sorts should feel consistent.
      if (sortBy === "price-low") return aCount - bCount;
      if (sortBy === "price-high") return bCount - aCount;

      // For newest/popular/rating/other, show higher related-product suppliers first.
      return bCount - aCount;
    });
    return copy;
  }, [suppliers, sortBy]);
  const showingText = useMemo(() => {
    const total = Number(pagination.total || products.length || 0);
    const currentPage = Number(pagination.page || 1);
    const limit = Number(pagination.limit || itemsPerPage || 20);
    if (total === 0) return "Showing 0 results";
    const start = (currentPage - 1) * limit + 1;
    const end = Math.min(currentPage * limit, total);
    return `Showing ${start}-${end} of ${total} results`;
  }, [pagination, products.length, itemsPerPage]);

  const pageTitle = useMemo(() => {
    if (parsedCategoryPath.length > 0) {
      return parsedCategoryPath[parsedCategoryPath.length - 1].name;
    }
    if (searchQuery.trim()) return "Search Results";
    return "All Products";
  }, [parsedCategoryPath, searchQuery]);

  /** Matches AuthProvider: super_admin/company_admin → admin (valid navigationConfigs key). */
  const headerRole: "admin" | "buyer" | "seller" =
    authUserRole ?? "buyer";

  return (
    <div className="min-h-screen bg-[#f4f6f8]">
      <div ref={headerWrapperRef} className="sticky top-0 z-50">
        <BizcoverIndiaFigmaHeader
          className="!static"
          searchQuery={searchQuery}
          onSearchChange={handleSearchChange}
          onSearchSubmit={handleSearch}
          onExploreCategoriesClick={(anchorRect) => {
            const fallbackOffset = Math.ceil(
              headerWrapperRef.current?.getBoundingClientRect().bottom || megaMenuTopOffset
            );
            const anchorOffset = anchorRect ? Math.ceil(anchorRect.bottom) : fallbackOffset;
            const offsetWithGap = Math.max(0, anchorOffset + 4);
            setMegaMenuTopOffset(offsetWithGap);
            setMegaMenuHoveredCategoryId(null);
            setMegaMenuOpen((prev) => !prev);
          }}
        />
      </div>
      <HomepageCategoryMegaMenu
        categories={categories as Category[]}
        isOpen={megaMenuOpen}
        onOpenChange={setMegaMenuOpen}
        hoveredCategoryId={megaMenuHoveredCategoryId}
        onHoverCategory={setMegaMenuHoveredCategoryId}
        onNavigate={(path) => navigate(path)}
        topOffsetPx={megaMenuTopOffset}
      />
      <main className="mx-auto w-full max-w-[1920px] px-4 py-6 lg:px-36">
      {/* {!categoryPathParam && (
        <section className="bg-white py-8 border-b border-gray-200">
          <div className="container mx-auto px-4">
            <div className="max-w-3xl mx-auto">
              <div className="flex gap-2">
                <div className="flex-1 relative">
                  <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-5 w-5 text-gray-400 pointer-events-none" />
                  <Input
                    type="text"
                    placeholder="Search products, suppliers..."
                    value={searchQuery}
                    onChange={(e) => handleSearchChange(e.target.value)}
                    onKeyPress={(e) => {
                      if (e.key === "Enter") {
                        handleSearch();
                      }
                    }}
                    className="pl-10 pr-10 h-12 text-base"
                  />
                  {searchQuery && (
                    <button
                      onClick={handleClearSearch}
                      className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600 transition-colors"
                      type="button"
                    >
                      <X className="h-4 w-4" />
                    </button>
                  )}
                </div>
                <Button
                  onClick={handleSearch}
                  variant="primary"
                  className="h-12 px-6 whitespace-nowrap bg-[#F97316] hover:bg-[#ea6820]"
                >
                  <Search className="h-4 w-4 mr-2" />
                  Search
                </Button>
              </div>
            </div>
          </div>
        </section>
      )} */}
        <div className="mb-3 flex items-center gap-2 text-[12px] text-[#919eab]">
          <span className="inline-flex h-4 w-4 items-center justify-center rounded-full border border-[#dfe3e8]">
            <Building2 className="h-3 w-3" />
          </span>
          <button
            type="button"
            onClick={() => navigate("/")}
            className="hover:text-[#637381]"
          >
            Home
          </button>
          <ChevronRight className="h-3.5 w-3.5" />
          {parsedCategoryPath.length > 0 ? (
            <>
              {parsedCategoryPath.map((category, index) => (
                <React.Fragment key={category.id}>
                  {index > 0 && <ChevronRight className="h-3.5 w-3.5" />}
                  {index === parsedCategoryPath.length - 1 ? (
                    <span className="text-[#637381]">{category.name}</span>
                  ) : (
                    <button
                      type="button"
                      onClick={() => handleBreadcrumbClick(index + 1)}
                      className="hover:text-[#637381]"
                    >
                      {category.name}
                    </button>
                  )}
                </React.Fragment>
              ))}
            </>
          ) : (
            <span className="text-[#637381]">Products</span>
          )}
        </div>

        <div className="mb-4 flex flex-wrap items-baseline justify-between gap-2 border-b border-gray-200 pb-3">
          <h1 className="text-[22px] font-semibold tracking-tight text-[#1A2A3D] sm:text-[24px]">
            {pageTitle}
          </h1>
          <span className="text-[12px] text-gray-500">{showingText}</span>
        </div>

          <section className="grid gap-4 lg:grid-cols-[260px_1fr] items-start">
            <aside
              className={`rounded-lg border border-gray-200 bg-white p-4 shadow-sm sm:p-5 self-start ${
                showMobileFilters ? "" : "hidden lg:block"
              }`}
            >
              <div className="mb-4 flex items-center justify-between border-b border-gray-200 pb-3">
                <h3 className="flex items-center gap-2 text-[13px] font-semibold uppercase tracking-wide text-gray-700">
                  <span>Filters</span>
                  {activeFilterCount > 0 && (
                    <span className="inline-flex h-5 min-w-[20px] items-center justify-center rounded-full bg-[#F97316] px-1.5 text-[10px] font-semibold text-white">
                      {activeFilterCount}
                    </span>
                  )}
                </h3>
                <button
                  type="button"
                  onClick={handleSidebarClearCategories}
                  className="text-[11px] font-medium text-gray-500 hover:text-[#F97316]"
                >
                  Clear all
                </button>
              </div>
              <div className="space-y-4">
                <div>
                  <button
                    type="button"
                    className="mb-2 flex w-full items-center justify-between"
                    onClick={() => setIsSidebarIndustryExpanded((prev) => !prev)}
                  >
                    <span className="text-[12px] font-semibold uppercase tracking-wide text-gray-700">
                      Industries
                    </span>
                    <ChevronDown
                      className={`h-4 w-4 text-[#6b7280] ${isSidebarIndustryExpanded ? "" : "-rotate-90"}`}
                    />
                  </button>
                  {isSidebarIndustryExpanded && (
                    <>
                      {showAllSidebarIndustries && (
                        <input
                          value={sidebarIndustrySearch}
                          onChange={(e) => setSidebarIndustrySearch(e.target.value)}
                          placeholder="Search industries"
                          className="mb-2 h-8 w-full rounded border border-[#e6ebf0] px-2 text-xs outline-none"
                        />
                      )}
                      <div className="max-h-[220px] space-y-1.5 overflow-auto pr-1">
                        <label className="flex cursor-pointer items-center gap-2 text-xs text-[#374151]">
                          <input
                            type="radio"
                            name="sidebar-industry"
                            checked={!categoryPathParam}
                            onChange={() => commitCategoryPathNodes(null)}
                          />
                          <span>All industries</span>
                        </label>
                        {sidebarIndustryFilterList.map((cat: any) => (
                          <label
                            key={String(cat.id)}
                            className="flex cursor-pointer items-center gap-2 text-xs text-[#374151]"
                          >
                            <input
                              type="radio"
                              name="sidebar-industry"
                              checked={parsedCategoryPath[0]?.id === cat.id}
                              onChange={() => commitCategoryPathNodes([cat])}
                            />
                            <span>{cat.name}</span>
                          </label>
                        ))}
                      </div>
                      {topIndustries.length > 8 && (
                        <button
                          type="button"
                          onClick={() => {
                            if (showAllSidebarIndustries) setSidebarIndustrySearch("");
                            setShowAllSidebarIndustries((prev) => !prev);
                          }}
                          className="mt-2 text-xs font-semibold text-[#F97316]"
                        >
                          {showAllSidebarIndustries ? "Show less" : "Show more"}
                        </button>
                      )}
                    </>
                  )}
                </div>

                {industryForSidebar &&
                industryForSidebar.children &&
                Array.isArray(industryForSidebar.children) &&
                industryForSidebar.children.length > 0 ? (
                  <div>
                    <button
                      type="button"
                      className="mb-2 flex w-full items-center justify-between"
                      onClick={() => setIsSidebarCategoryExpanded((prev) => !prev)}
                    >
                      <span className="text-[12px] font-semibold uppercase tracking-wide text-gray-700">
                        Categories
                      </span>
                      <ChevronDown
                        className={`h-4 w-4 text-[#6b7280] ${isSidebarCategoryExpanded ? "" : "-rotate-90"}`}
                      />
                    </button>
                    {isSidebarCategoryExpanded && (
                      <>
                        {showAllSidebarCategories && (
                          <input
                            value={sidebarCategorySearch}
                            onChange={(e) => setSidebarCategorySearch(e.target.value)}
                            placeholder="Search categories"
                            className="mb-2 h-8 w-full rounded border border-[#e6ebf0] px-2 text-xs outline-none"
                          />
                        )}
                        <div className="max-h-[220px] space-y-1.5 overflow-auto pr-1">
                          <label className="flex cursor-pointer items-center gap-2 text-xs text-[#374151]">
                            <input
                              type="radio"
                              name="sidebar-secondary"
                              checked={parsedCategoryPath.length === 1}
                              onChange={() =>
                                industryForSidebar &&
                                commitCategoryPathNodes([industryForSidebar])
                              }
                            />
                            <span>All in {industryForSidebar.name}</span>
                          </label>
                          {sidebarSecondaryFilterList.map((sub: any) => (
                            <label
                              key={String(sub.id)}
                              className="flex cursor-pointer items-center gap-2 text-xs text-[#374151]"
                            >
                              <input
                                type="radio"
                                name="sidebar-secondary"
                                checked={selectedSecondaryId === String(sub.id)}
                                onChange={() =>
                                  industryForSidebar &&
                                  commitCategoryPathNodes([industryForSidebar, sub])
                                }
                              />
                              <span>{sub.name}</span>
                            </label>
                          ))}
                        </div>
                        {industryForSidebar.children.length > 8 && (
                          <button
                            type="button"
                            onClick={() => {
                              if (showAllSidebarCategories) setSidebarCategorySearch("");
                              setShowAllSidebarCategories((prev) => !prev);
                            }}
                            className="mt-2 text-xs font-semibold text-[#F97316]"
                          >
                            {showAllSidebarCategories ? "Show less" : "Show more"}
                          </button>
                        )}
                      </>
                    )}
                  </div>
                ) : null}

                <SidebarFilterGroup title="Price Range">
                  <RangeFilter
                    min={filters.priceMin}
                    max={filters.priceMax}
                    hardMax={PRICE_MAX}
                    prefix="$"
                    onCommit={(lo, hi) =>
                      updateFilters({ priceMin: lo, priceMax: hi })
                    }
                  />
                </SidebarFilterGroup>

                <SidebarFilterGroup title="MOQ Range">
                  <RangeFilter
                    min={filters.moqMin}
                    max={filters.moqMax}
                    hardMax={MOQ_MAX}
                    onCommit={(lo, hi) =>
                      updateFilters({ moqMin: lo, moqMax: hi })
                    }
                  />
                </SidebarFilterGroup>
              </div>
            </aside>

            <div className="min-w-0 rounded-lg bg-white p-4 shadow-sm sm:p-5">
              {activeTab === "products" && (
                <div className="mb-4 flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
                  <button
                    type="button"
                    onClick={() => setShowMobileFilters((p) => !p)}
                    className="inline-flex h-9 items-center justify-center gap-2 rounded-full border border-gray-200 bg-white px-3 text-[12px] font-semibold text-gray-700 hover:border-[#F97316] hover:text-[#F97316] lg:hidden"
                    aria-expanded={showMobileFilters}
                  >
                    <SlidersHorizontal className="h-4 w-4" />
                    <span>Filters</span>
                    {activeFilterCount > 0 && (
                      <span className="inline-flex h-5 min-w-[20px] items-center justify-center rounded-full bg-[#F97316] px-1.5 text-[10px] font-semibold text-white">
                        {activeFilterCount}
                      </span>
                    )}
                  </button>
                  <div className="flex w-full flex-wrap items-center gap-2 sm:w-auto sm:justify-end">
                    <label className="relative w-full sm:w-[240px]">
                      <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-gray-400" />
                      <input
                        type="text"
                        value={searchQuery}
                        onChange={(e) => handleSearchChange(e.target.value)}
                        onKeyDown={(e) => {
                          if (e.key === "Enter") handleSearch();
                        }}
                        placeholder="Search products"
                        className="h-9 w-full rounded-full border border-gray-200 bg-white pl-9 pr-3 text-[12px] text-gray-700 placeholder:text-gray-400 outline-none focus:border-[#F97316] focus:ring-2 focus:ring-[#F97316]/15"
                      />
                    </label>
                    <span className="text-[12px] text-gray-500">Sort</span>
                    <select
                      value={sortBy}
                      onChange={(e) => handleSortChange(e.target.value)}
                      className="h-9 rounded-full border border-gray-200 bg-white px-3 text-[12px] text-gray-700 outline-none focus:ring-2 focus:ring-[#F97316]/15"
                    >
                      <option value="relevance">Relevance</option>
                      <option value="price-low">Price: Low to High</option>
                      <option value="price-high">Price: High to Low</option>
                      <option value="newest">Newest</option>
                      <option value="popular">Popular</option>
                      <option value="rating">Rating</option>
                    </select>
                  </div>
                </div>
              )}

              {activeTab === "products" && activeFilters.length > 0 && (
                <div className="mb-4 -mx-4 sm:-mx-5">
                  <ActiveFiltersBar
                    filters={activeFilters}
                    onClearAll={handleClearFilters}
                  />
                </div>
              )}

          {/* Product/Supplier List Header */}
          {(activeTab === "suppliers") && (
            <ProductListHeader
              totalResults={suppliers.length}
              currentPage={1}
              itemsPerPage={Math.max(suppliers.length, 1)}
              sortBy={sortBy}
              onSortChange={handleSortChange}
              onItemsPerPageChange={undefined}
              currentView={suppliersView}
              onViewChange={setSuppliersView}
              onFilterToggle={undefined}
              itemsPerPageOptions={[20, 40, 60, 100]}
            />
          )}

          {/* Products Grid */}
          <div className="flex-1">
            {loading && (
              <div className="text-center py-16">
                <p className="text-gray-500 text-lg">Loading products...</p>
              </div>
            )}

            {error && !loading && (
              <div className="text-center py-16">
                <p className="text-red-500 text-lg">{error}</p>
                <Button
                  variant="outline"
                  className="mt-4"
                  onClick={() => {
                    setError(null);
                    setPagination((prev) => ({ ...prev, page: 1 }));
                  }}
                >
                  Try Again
                </Button>
              </div>
            )}

            {!loading &&
              !error &&
              activeTab === "products" &&
              products.length === 0 && (
                <div className="text-center py-16">
                  <p className="text-gray-500 text-lg">
                    No products found matching your search.
                  </p>
                </div>
              )}

            {!loading && !error && activeTab === "products" && products.length > 0 && (
              <>
                <div className="grid items-start gap-3 grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 2xl:grid-cols-4">
                    {products.map((product, index) => (
                      <article
                        key={`${product.id}-${index}`}
                        role="group"
                        tabIndex={0}
                        aria-label={`${product.title}. View product.`}
                        className="group min-w-0 cursor-pointer rounded-xl border border-gray-200 bg-white p-2.5 transition-shadow hover:shadow-md outline-none focus-visible:ring-2 focus-visible:ring-[#F97316] focus-visible:ring-offset-2"
                        onClick={() => handleContactSupplier(product.id)}
                        onKeyDown={(e) => {
                          if (e.key !== "Enter" && e.key !== " ") return;
                          if ((e.target as HTMLElement).closest("button, a")) {
                            return;
                          }
                          e.preventDefault();
                          handleContactSupplier(product.id);
                        }}
                      >
                        {(() => {
                          const safePrice = getSafePrice(product.price);
                          const originalPrice = safePrice * 1.25;
                          return (
                            <>
                        <div className="relative aspect-[4/3] overflow-hidden rounded-lg border border-gray-100 bg-gray-50">
                          {product.image ? (
                            <img
                              src={product.image}
                              alt={product.title}
                              className="h-full w-full object-cover"
                            />
                          ) : (
                            <div className="flex h-full w-full items-center justify-center text-gray-400 text-sm">
                              No Image Preview
                            </div>
                          )}
                        </div>

                        <div className="mt-3">
                          <h3 className="line-clamp-2 min-h-[2.5rem] text-sm font-semibold text-gray-900 break-words">
                            {product.title}
                          </h3>

                          <div className="mt-2 flex min-w-0 items-center gap-1 text-xs text-gray-500">
                            <span className="truncate block">
                              {product.supplier?.name || "Supplier"}
                            </span>
                            {product.supplier?.verified && (
                              <span className="inline-flex items-center text-green-700">
                                <CheckCircle2 className="h-3.5 w-3.5" />
                              </span>
                            )}
                          </div>

                          <div className="mt-2">
                            <div className="flex items-end gap-2">
                              <span className="text-base font-bold text-[#F97316]">
                                ${safePrice.toFixed(2)}
                              </span>
                            </div>
                            {product.unitPrice ? (
                              <p className="mt-0.5 text-xs text-gray-600">{product.unitPrice}</p>
                            ) : null}
                          </div>

                          <div className="mt-3 flex items-center gap-1.5">
                            {canShowFavorites && (
                              <button
                                type="button"
                                disabled={favoriteLoadingIds.has(String(product.id))}
                                onClick={(ev) => {
                                  ev.stopPropagation();
                                  handleToggleFavorite(String(product.id));
                                }}
                                className="inline-flex h-7 w-7 items-center justify-center rounded-full border border-gray-200 text-gray-500 transition-colors hover:border-[#F97316] hover:text-[#F97316]"
                                aria-label="Save product"
                              >
                                <Heart
                                  className={`h-4 w-4 ${
                                    favoriteIds.has(String(product.id))
                                      ? "fill-[#F97316] text-[#F97316]"
                                      : ""
                                  }`}
                                />
                              </button>
                            )}
                            
                            <Button
                              size="sm"
                              className="h-7 min-w-0 flex-1 rounded-full bg-[#F97316] px-2 text-white hover:bg-[#ea6820] whitespace-nowrap"
                              onClick={(ev) => {
                                ev.stopPropagation();
                                handleContactSupplier(product.id);
                              }}
                            >
                              <Eye className="mr-1 h-3.5 w-3.5" />
                              View Product
                            </Button>
                          </div>
                        </div>
                            </>
                          );
                        })()}
                      </article>
                    ))}
                  </div>

                {/* Pagination */}
                {pagination.totalPages > 1 && (
                  <div className="mt-6 border-t border-[#eef1f4] pt-4">
                    <div className="flex flex-wrap items-center justify-between gap-3">
                      <p className="text-xs font-medium text-[#637381]">
                        {showingText}
                      </p>
                      <div className="inline-flex items-center gap-1.5 rounded-full  bg-white p-1">
                        <button
                          type="button"
                          className="inline-flex h-8 items-center gap-1 rounded-full border border-[#dfe3e8] bg-white px-3 text-xs font-semibold text-[#334155] disabled:opacity-50"
                          disabled={!pagination.hasPrev}
                          onClick={() => handlePageChange(pagination.page - 1)}
                        >
                          Prev
                        </button>
                      {Array.from(
                        { length: Math.min(5, pagination.totalPages) },
                        (_, i) => {
                          let pageNum: number;
                          if (pagination.totalPages <= 5) {
                            pageNum = i + 1;
                          } else if (pagination.page <= 3) {
                            pageNum = i + 1;
                          } else if (
                            pagination.page >=
                            pagination.totalPages - 2
                          ) {
                            pageNum = pagination.totalPages - 4 + i;
                          } else {
                            pageNum = pagination.page - 2 + i;
                          }
                          return (
                            <button
                              type="button"
                              key={pageNum}
                              className={`inline-flex h-8 min-w-8 items-center justify-center rounded-full px-2 text-xs font-semibold ${
                                pagination.page === pageNum
                                  ? "bg-[#F97316] text-white"
                                  : "border border-[#dfe3e8] bg-white text-[#334155]"
                              }`}
                              onClick={() => handlePageChange(pageNum)}
                            >
                              {pageNum}
                            </button>
                          );
                        }
                      )}
                        <button
                          type="button"
                          className="inline-flex h-8 items-center gap-1 rounded-full border border-[#dfe3e8] bg-white px-3 text-xs font-semibold text-[#334155] disabled:opacity-50"
                          disabled={!pagination.hasNext}
                          onClick={() => handlePageChange(pagination.page + 1)}
                        >
                          Next
                        </button>
                      </div>
                    </div>
                  </div>
                )}
              </>
            )}

            {!loading && !error && activeTab === "suppliers" && suppliers.length === 0 && (
              <div className="text-center py-16">
                <p className="text-gray-500 text-lg">
                  No suppliers found for this category.
                </p>
              </div>
            )}

            {!loading && !error && activeTab === "suppliers" && suppliers.length > 0 && (
              <>
                {suppliersView === "grid" ? (
                  <div className="grid items-start grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
                    {sortedSuppliers.map((supplier: any) => (
                      <div
                        key={supplier.id}
                        className="relative rounded-xl border border-gray-200 bg-white p-4 hover:shadow-sm transition-shadow"
                        onMouseEnter={() =>
                          setHoveredSupplierId(String(supplier.id))
                        }
                        onMouseLeave={() => setHoveredSupplierId(null)}
                      >
                        <div className="flex items-start justify-between gap-3">
                          <div className="flex items-start gap-3 min-w-0 flex-1">
                            <SupplierTabLogo
                              logo={supplier.logo}
                              name={supplier.name}
                            />
                            <div className="min-w-0">
                              <p className="text-base font-semibold text-gray-900 line-clamp-1">
                                {supplier.name}
                              </p>
                              <div className="mt-1 text-xs text-gray-500">
                                <span>{supplier.country}</span>
                                {supplier.yearsInBusiness != null ? (
                                  <span className="text-gray-500">
                                    {" "}
                                    · {supplier.yearsInBusiness}{" "}
                                    {supplier.yearsInBusiness === 1
                                      ? "year"
                                      : "years"}
                                  </span>
                                ) : null}
                              </div>
                            </div>
                          </div>
                        </div>

                        <div className="mt-4 border-t border-gray-100 pt-3">
                          <div className="flex gap-3 justify-start flex-wrap">
                            {supplier.products.slice(0, 3).map((p: any) => (
                              <div
                                key={p.id}
                                className="flex flex-col gap-1.5 w-[5.25rem] flex-shrink-0"
                              >
                                <div className="w-full aspect-[5/4] rounded border border-gray-200 bg-gray-100 overflow-hidden flex items-center justify-center">
                                  {p.image ? (
                                    <img
                                      src={p.image}
                                      alt=""
                                      className="h-full w-full object-cover"
                                    />
                                  ) : (
                                    <Building2 className="h-4 w-4 text-gray-400" />
                                  )}
                                </div>
                                <p
                                  className="text-[11px] leading-snug text-gray-800 line-clamp-2 text-center px-0.5"
                                  title={p.name}
                                >
                                  {p.name}
                                </p>
                              </div>
                            ))}
                          </div>
                        </div>

                        <div
                          className={`mt-3 overflow-hidden transition-all duration-200 ${
                            hoveredSupplierId === String(supplier.id)
                              ? "max-h-20 opacity-100 pointer-events-auto"
                              : "max-h-0 opacity-0 pointer-events-none"
                          }`}
                        >
                          <Button
                            size="sm"
                            className="w-full h-8 rounded-full bg-[#F97316] hover:bg-[#ea6820] text-white"
                            onClick={() =>
                              handleViewSupplierProfile(
                                String(supplier.profileSellerId || supplier.id)
                              )
                            }
                          >
                            View Profile
                          </Button>
                        </div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="flex flex-col gap-4">
                    {sortedSuppliers.map((supplier: any) => (
                      <SupplierListCard
                        key={supplier.id}
                        supplier={supplier}
                        onViewProfile={() =>
                          handleViewSupplierProfile(
                            String(supplier.profileSellerId || supplier.id)
                          )
                        }
                      />
                    ))}
                  </div>
                )}
              </>
            )}
          </div>
            </div>
          </section>
      </main>

      <BizcoverIndiaFigmaFooter />
    </div>
  );
};

export default ProductSearch;
